import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/global/components/ui/card'
import { Tooltip } from '@/global/components/ui/tooltip'
import { cn, formatarMoeda, formatarNumero } from '@/lib/utils'
import dayjs from 'dayjs'
import { Link } from 'global/components/Link'
import Skeleton from 'global/components/Skeleton'
import { periodicidade } from 'locales/plan'
import { TriangleAlertIcon } from 'lucide-react'
import { usePlanosContext } from 'pages/Planos.context'
import { clientes } from 'service/api'

export default function GridDados() {
    return (
        <div className="grid w-full shrink-0 grid-cols-1 gap-4 md:grid-cols-2">
            <Card className="min-w-0">
                <DadosContrato />
            </Card>
            <Card className="min-w-0">
                <DadosPagamento />
            </Card>
        </div>
    )
}

function TituloBloco({ children }: { children: React.ReactNode }) {
    return (
        <CardHeader className="px-4 py-3 pb-1">
            <CardTitle>{children}</CardTitle>
        </CardHeader>
    )
}

function ListaDados({ children }: { children: React.ReactNode }) {
    return (
        <CardContent className="px-4 pb-3">
            <dl className="divide-y text-sm">{children}</dl>
        </CardContent>
    )
}

function LinhaDado({
    rotulo,
    children,
    className,
}: {
    rotulo: string
    children: React.ReactNode
    className?: string
}) {
    return (
        <div className="flex items-baseline justify-between gap-4 py-1">
            <dt className="shrink-0 text-muted-foreground">{rotulo}</dt>
            {/* `anywhere`: e-mail e número de cartão não têm onde quebrar e vazavam do bloco. */}
            <dd
                className={cn(
                    'min-w-0 text-right font-medium [overflow-wrap:anywhere]',
                    className,
                )}
            >
                {children}
            </dd>
        </div>
    )
}

function LinhasCarregando({ quantidade }: { quantidade: number }) {
    return (
        <CardContent className="px-4 pb-3" aria-busy="true">
            <div className="divide-y">
                {Array.from({ length: quantidade }, (_, indice) => (
                    <div
                        key={indice}
                        className="flex items-center justify-between gap-4 py-1.5"
                    >
                        <Skeleton className="h-4 w-28 flex-grow-0" />
                        <Skeleton className="h-4 w-24 flex-grow-0" />
                    </div>
                ))}
            </div>
        </CardContent>
    )
}

function DadosContrato() {
    const { assinatura, planos, isLoading } = usePlanosContext()
    const meuPlano = planos?.find(
        it =>
            it.limiteFuncionarios ===
                Number(assinatura?.additionalInfo?.replace(/\D/g, '')) &&
            it.periodicity === assinatura?.periodicity,
    )

    const { data, isLoading: isLoadingLimites } = clientes.useFetchById<{
        ativos: number
        limite: number
    }>('limites')

    const { ativos, limite } = data || { ativos: 0, limite: 0 },
        isBloqueado = ativos > limite,
        isMax = ativos === limite

    if (isLoading || isLoadingLimites) {
        return (
            <>
                <TituloBloco>Dados do contrato</TituloBloco>
                <LinhasCarregando quantidade={6} />
            </>
        )
    }

    return (
        <>
            <TituloBloco>Dados do contrato</TituloBloco>
            <ListaDados>
                <LinhaDado
                    rotulo="Funcionários ativos"
                    className={(() => {
                        if (isBloqueado) return 'text-destructive'
                        if (isMax) return 'text-primary'
                        return undefined
                    })()}
                >
                    <span className="inline-flex items-center gap-1">
                        <span className="inline-flex items-baseline">
                            {formatarNumero(ativos)}
                            <span className="text-xs text-muted-foreground select-none">
                                /{formatarNumero(limite)}
                            </span>
                        </span>
                        {isBloqueado && (
                            <Tooltip
                                title={
                                    <span>
                                        Você ultrapassou o limite de
                                        funcionários ativos.
                                        <br />
                                        <br />
                                        Altere seu plano ou exclua funcionários
                                        para continuar usando o sistema.
                                        <br />
                                        Depois faça login novamente para que o
                                        sistema seja liberado.
                                    </span>
                                }
                            >
                                <TriangleAlertIcon className="size-4 text-error" />
                            </Tooltip>
                        )}
                        {isMax && (
                            <Tooltip
                                title={
                                    <span>
                                        Você atingiu o limite de funcionários
                                        ativos.
                                        <br />
                                        Para cadastrar mais funcionários, altere
                                        seu plano.
                                    </span>
                                }
                            >
                                <TriangleAlertIcon className="size-4 text-warning" />
                            </Tooltip>
                        )}
                    </span>
                </LinhaDado>
                {assinatura ? (
                    <>
                        <LinhaDado rotulo="Plano">
                            {(() => {
                                if (!meuPlano) return 'Plano não encontrado'
                                if (meuPlano.limiteFuncionarios == 0)
                                    return 'Especial'
                                return `até ${formatarNumero(meuPlano.limiteFuncionarios)} funcionários`
                            })()}
                        </LinhaDado>
                        <LinhaDado rotulo="Periodicidade">
                            {periodicidade[assinatura.periodicity]}
                        </LinhaDado>
                        <LinhaDado rotulo="1ª cobrança">
                            {dayjs(assinatura.firstPayDayDate).format(
                                'DD/MM/YYYY',
                            )}
                        </LinhaDado>
                        <LinhaDado rotulo="Transações">
                            {assinatura.quantity === 0 ? (
                                <Tooltip
                                    title={
                                        <span>
                                            Faltando 30 dias para o vencimento,
                                            a
                                            <br />
                                            próxima transação é adicionada
                                            <br />
                                            automaticamente no sistema. O
                                            sistema só
                                            <br />
                                            para de lançar automaticamente
                                            quando o
                                            <br />
                                            contrato é cancelado manualmente.
                                            <br />
                                        </span>
                                    }
                                >
                                    <Link className="cursor-help">
                                        Indeterminado
                                    </Link>
                                </Tooltip>
                            ) : (
                                formatarNumero(assinatura.quantity)
                            )}
                        </LinhaDado>
                        <LinhaDado rotulo="E-mail financeiro">
                            {assinatura.Customer?.emails?.at(-1)}
                        </LinhaDado>
                    </>
                ) : (
                    <p className="py-1.5 text-muted-foreground">
                        Não está vendo os dados sobre sua assinatura? Ela pode
                        ter sido cancelada ou estar com algum problema. Entre em
                        contato com o suporte para mais informações.
                    </p>
                )}
            </ListaDados>
        </>
    )
}

function DadosPagamento() {
    const { assinatura, isLoading } = usePlanosContext()

    if (isLoading) {
        return (
            <>
                <TituloBloco>Dados de pagamento</TituloBloco>
                <LinhasCarregando quantidade={5} />
            </>
        )
    }

    return (
        <>
            <TituloBloco>Dados de pagamento</TituloBloco>
            <ListaDados>
                {!assinatura && (
                    <p className="py-1.5 text-muted-foreground">
                        Nenhuma forma de pagamento ativa.
                    </p>
                )}
                {assinatura?.PaymentMethodBoleto && (
                    <>
                        <LinhaDado rotulo="Forma de pagamento">
                            Boleto/PIX
                        </LinhaDado>
                        <LinhaDado rotulo="Próxima cobrança">
                            {formatarMoeda(assinatura.value)}
                        </LinhaDado>
                        <LinhaDado rotulo="Multa por atraso">
                            {formatarNumero(
                                assinatura.PaymentMethodBoleto.fine! / 100,
                            )}
                            %
                        </LinhaDado>
                        <LinhaDado rotulo="Juros ao mês">
                            {formatarNumero(
                                assinatura.PaymentMethodBoleto.interest! / 100,
                            )}
                            %
                        </LinhaDado>
                        <LinhaDado rotulo="Prazo para 2ª via">
                            {formatarNumero(
                                assinatura.PaymentMethodBoleto.deadlineDays,
                            )}{' '}
                            dias
                        </LinhaDado>
                    </>
                )}
                {assinatura?.PaymentMethodCreditCard && (
                    <>
                        <LinhaDado rotulo="Forma de pagamento">
                            Cartão de crédito
                        </LinhaDado>
                        <LinhaDado rotulo="Próxima cobrança">
                            {formatarMoeda(assinatura.value)}
                        </LinhaDado>
                        <LinhaDado rotulo="Cartão">
                            {
                                assinatura.PaymentMethodCreditCard.Card?.Brand
                                    ?.name
                            }{' '}
                            ****{' '}
                            {assinatura.PaymentMethodCreditCard.Card?.number?.slice(
                                -4,
                            )}
                        </LinhaDado>
                        <LinhaDado rotulo="Multa por atraso">
                            {formatarMoeda(1600)}
                        </LinhaDado>
                        <LinhaDado rotulo="Juros ao mês">
                            {formatarMoeda(800)} ({formatarMoeda(27)} ao dia)
                        </LinhaDado>
                    </>
                )}
            </ListaDados>
        </>
    )
}
